/**
 * Pure age-band helpers shared by every minors-13plus surface (mission plan
 * ~/plans/minors-13plus-compliance-2026-09-14.md wave 2): Referrals gating,
 * Settings > Account > Age & birthday, and the analytics "minor mode" gate.
 * Mirrors the backend's single source of truth, `users/services/age_policy.py`
 * (Backend PR #3170) — `AgeBand`/`AgeSource` choices and the is_minor /
 * is_adult_verified semantics ("unknown" is neither adult nor minor).
 *
 * Deliberately has NO opinion on MINORS_POLICY_ENABLED — every call site
 * gates on that flag itself (same contract as age_policy.py on the backend),
 * so this module is safe to import unconditionally.
 */

export const AGE_BAND = {
  UNKNOWN: "unknown",
  UNDER_13: "under_13",
  AGE_13_15: "13_15",
  AGE_16_17: "16_17",
  ADULT: "adult",
};

export const AGE_SOURCE = {
  STORE_IOS: "store_ios",
  STORE_ANDROID: "store_android",
  SELF_DECLARED: "self_declared",
  DOB_SETTINGS: "dob_settings",
  ADMIN: "admin",
};

const MINOR_AGE_BANDS = [
  AGE_BAND.UNDER_13,
  AGE_BAND.AGE_13_15,
  AGE_BAND.AGE_16_17,
];

const STORE_AGE_SOURCES = [AGE_SOURCE.STORE_IOS, AGE_SOURCE.STORE_ANDROID];

/** True only for a KNOWN minor band (under_13/13_15/16_17) — "unknown" is
 * NOT treated as minor, matching age_policy.is_minor on the backend. */
export const isMinorAgeBand = (userProfile) =>
  MINOR_AGE_BANDS.includes(userProfile?.age_band);

/** True only for the exact "adult" band — "unknown" must NOT satisfy an
 * adult-only gate, matching age_policy.is_adult_verified on the backend. */
export const isAdultVerified = (userProfile) =>
  userProfile?.age_band === AGE_BAND.ADULT;

/** True when the recorded age signal came from a platform (App Store / Play)
 * rather than a self-typed date — PATCH /me/dob/ refuses to override this. */
export const isStoreVerifiedAge = (userProfile) =>
  STORE_AGE_SOURCES.includes(userProfile?.age_source);
